import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router-dom'
import { useAuth, useProfile } from '@/store'

interface ProtectedRouteProps {
  children: ReactNode
}

export default function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, initialized, loading } = useAuth()
  const { hasProfile } = useProfile()
  const { t } = useTranslation()

  const loadingScreen = (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <div className="text-foreground">{t('common.loading')}</div>
    </div>
  )

  if (!initialized || loading) {
    return loadingScreen
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }

  // Authenticated, but the /users/{uid} profile-doc check hasn't resolved yet — show the
  // loading state rather than flashing a data-denied /home.
  if (hasProfile === null) {
    return loadingScreen
  }

  // Authenticated with NO profile doc = an inert account that never redeemed an invite
  // (e.g. Google/Apple via /login, or an abandoned claim). Route to the No-Invite screen.
  if (!hasProfile) {
    return <Navigate to="/no-invite" replace />
  }

  return children
}
